/**
 * html2canvas `onclone` hook.
 *
 * html2canvas (including the copy bundled in html2pdf 0.10.1) parses colours with
 * its own tokenizer and throws `Attempting to parse an unsupported color function`
 * on anything newer than rgb()/hsl(). Chrome reports `color-mix()` as
 * `color(srgb r g b / a)`, so any captured element styled with color-mix (the
 * annotation highlights in LexicalTextEditor/themes/anno.js, table rows in
 * TableElem) aborts the whole capture.
 *
 * This rewrites those computed values to plain rgba() as inline !important
 * declarations on the throwaway clone document html2canvas renders from. The
 * live page is untouched.
 */

const COLOR_PROPS = [
  "color",
  "background-color",
  "background-image",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "outline-color",
  "text-decoration-color",
  "box-shadow",
  "text-shadow",
  "fill",
  "stroke",
];

const SRGB_FN = /color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.%]+))?\s*\)/g;

const srgbToRgba = (value) =>
  value.replace(SRGB_FN, (_, r, g, b, a) => {
    const channel = (x) => Math.round(parseFloat(x) * 255);
    const alpha = a === undefined ? 1 : a.endsWith("%") ? parseFloat(a) / 100 : parseFloat(a);
    return `rgba(${channel(r)}, ${channel(g)}, ${channel(b)}, ${alpha})`;
  });

export const inlineLegacyColorsForCapture = (clonedDoc) => {
  // Freeze transitions before touching any colour. Setting a new background on an
  // element that transitions background-color starts that transition in the clone,
  // and Chrome serialises an in-flight colour transition as oklab(), which
  // html2canvas rejects just the same.
  const freeze = clonedDoc.createElement("style");
  freeze.textContent = "* { transition: none !important; animation: none !important; }";
  clonedDoc.head.appendChild(freeze);

  const view = clonedDoc.defaultView;
  clonedDoc.querySelectorAll("*").forEach((el) => {
    const computed = view.getComputedStyle(el);
    COLOR_PROPS.forEach((prop) => {
      const value = computed.getPropertyValue(prop);
      if (value.includes("color(")) {
        el.style.setProperty(prop, srgbToRgba(value), "important");
      }
    });
  });
};
